import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import Cookies from 'js-cookie';
import { JWT_TYPE_NAME, ReefHeaders, ReefTokenService } from '@seco/core';
import { CreditLineAgency, ReadCreditLineStatusResponse } from './avc-credit-line-status.model';
import { CreditLineMockBackEndService } from './mock-backend-avc-credit-line.service';
import { isOn } from 'feature-toggle-service';

@Injectable({ providedIn: 'root' })
export class BackendAvcCreditLineService {

  readonly DATE_FORMAT = 'YYYY-MM-DD';
  readonly avcCreditLineUrl = '/db/dashboard/v1/creditline/data';
  readonly avcCreditLineUrlS3j = '/aa/api/v1/creditline/data';
  readonly creditlineS3jEnabled = isOn('AD_AVC_CREDIT_LINE_S3J_A');
  readonly url = {
    creditLine: () => {
      if (this.creditlineS3jEnabled) {
        return this.avcCreditLineUrlS3j;
      }
      return this.avcCreditLineUrl;
    }
  };

  constructor(
    private readonly http: HttpClient,
    private readonly creditLineMockBackendService: CreditLineMockBackEndService,
    private readonly reeftokenservice: ReefTokenService
  ) { }

  getAvcCreditLineStatus(officeId?: string, shouldMock = false): Observable<ReadCreditLineStatusResponse> {
    if (shouldMock) {
      if (officeId === 'NCE1A2201') {
        return of(this.creditLineMockBackendService.getActiveCreditLineStatusNoNetwork(officeId));
      } else if (officeId === 'NCE1A2345') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelNetworkParentSplit('NCE1A2345'));
      } else if (officeId === 'NCE1A2200') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelNetworkParentSplitChildNoAmount('NCE1A2200'));
      } else if (officeId === 'NCE1A2202') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelNetworkParentSplitChildNotCreditLine('NCE1A2202'));
      } else if (officeId === 'NCE1A2207') {
        return of(this.creditLineMockBackendService.getCreditLineStatusNotCreditLineCustomer('NCE1A2207'));
      } else if (officeId === 'NCE1A2204') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelSharedNetworkParent('NCE1A2204'));
      } else if (officeId === 'NCE1A2205') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelSharedNetworkChild('NCE1A2205'));
      } else if (officeId === 'NCE1A2206') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelSplitNetworkChild('NCE1A2206'));
      } else if (officeId === 'NCE1A2203') {
        return of(this.creditLineMockBackendService.getCreditLineStatusTwoLevelSharedNetworkChild('NCE1A2203'));
      }
    }

    if (this.creditlineS3jEnabled) {
      // Response is CreditLineAgency, wrap it in ReadCreditLineStatusResponse
      return this.http.get<CreditLineAgency>(
        this.avcUrl(this.url.creditLine()),
        { ...this.avcHeaders(), observe: 'response' }
      ).pipe(
        map(response => this.wrapCreditLineAgencyResponse(response.status, response.body))
      );
    } else {
      return this.http.get<ReadCreditLineStatusResponse>(this.avcUrl(this.url.creditLine()), this.avcHeaders());
    }
  }

  avcUrl(url: string): string {
    const sessionId = localStorage.getItem('JsessionId');
    return `${url};jsessionid=${sessionId}`;
  }

  avcHeaders(): {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } {
    const options: any = { withCredentials: true };
    const jwtMap = this.reeftokenservice.getTokens();
    const jwt = jwtMap?.get(ReefHeaders.AUTH)?.get(JWT_TYPE_NAME);
      const headers = new HttpHeaders({
        Authorization: `Bearer ${jwt}`
      });
      options.headers = headers;
    return options;
  }

  private wrapCreditLineAgencyResponse(httpStatus: number, agency: CreditLineAgency): ReadCreditLineStatusResponse {
    if (httpStatus !== 200) {
      return {
        statusCodeValue: httpStatus,
        body: {
          agencyData: null,
          requestedOfficeId: ''
        }
      };
    }
    return {
      statusCodeValue: httpStatus,
      body: {
        agencyData: agency,
        requestedOfficeId: agency.officeId
      }
    };
  }
}
